import React, { useEffect, useState } from 'react';
import { BadgeIndianRupee, LayoutGrid, ListChecks, ScanLine, Printer, MoreHorizontal, X, Plus, FileSpreadsheet, Coins, Settings, KeyRound, LogOut, Building2, Trash2 } from 'lucide-react';

export function useIsMobile() {
  const q = '(max-width: 767px)';
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = (e) => setM(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return m;
}

function NavBtn({ icon: Icon, label, onClick, testid, active }) {
  return (
    <button type="button" onClick={onClick} className={`flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[length:calc(var(--fs,1)*0.65625rem)] font-medium transition-colors ${active ? 'text-[hsl(var(--ring))]' : 'text-muted-foreground'}`} data-testid={testid}>
      <Icon size={20} strokeWidth={active ? 2.4 : 2} /> {label}
    </button>
  );
}

// Phone bottom bar: pieces, saved lists, scan (centre), print, more.
export function TagMobileNav({ onLists, onScan, onPrint, onMore, onSales }) {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/90 backdrop-blur-xl md:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="tagws-mobile-nav">
      <div className="mx-auto flex max-w-md items-end px-2">
        <NavBtn icon={LayoutGrid} label="Pieces" active onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} testid="tagws-mobile-nav-pieces" />
        <NavBtn icon={ListChecks} label="Lists" onClick={onLists} testid="tagws-mobile-nav-lists" />
        <div className="flex flex-1 justify-center">
          <button type="button" onClick={onScan} aria-label="Scan to build a list" className="-mt-6 grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/30 ring-4 ring-background transition-transform active:scale-95" data-testid="tagws-mobile-nav-scan"><ScanLine size={24} /></button>
        </div>
        {onSales ? <NavBtn icon={BadgeIndianRupee} label="Sales" onClick={onSales} testid="tagws-mobile-nav-sales" /> : <NavBtn icon={Printer} label="Print" onClick={onPrint} testid="tagws-mobile-nav-print" />}
        <NavBtn icon={MoreHorizontal} label="More" onClick={onMore} testid="tagws-mobile-nav-more" />
      </div>
    </nav>
  );
}

function SheetItem({ icon: Icon, label, hint, onClick, testid, danger }) {
  return (
    <button type="button" onClick={onClick} className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:bg-secondary ${danger ? 'text-destructive' : 'text-foreground'}`} data-testid={testid}>
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${danger ? 'bg-destructive/10' : 'bg-[hsl(var(--accent))] text-[hsl(var(--ring))]'}`}><Icon size={17} /></span>
      <span className="min-w-0"><span className="block text-sm font-semibold">{label}</span>{hint ? <span className="block truncate text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">{hint}</span> : null}</span>
    </button>
  );
}

// Phone "More" sheet: secondary actions + super admin tools.
export function TagMobileMore({ open, onClose, actions, superAdmin, companies = [], companyId, onCompany }) {
  if (!open) return null;
  const go = (fn) => () => { onClose(); fn?.(); };
  return (
    <div className="fixed inset-0 z-[150] md:hidden" data-testid="tagws-mobile-more-sheet">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-3xl border-t border-border bg-card p-3 shadow-2xl" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        <div className="mx-auto mb-2 h-1.5 w-10 rounded-full bg-border" />
        <div className="mb-1 flex items-center justify-between px-2">
          <span className="text-sm font-semibold text-foreground">More</span>
          <button onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground" data-testid="tagws-mobile-more-close"><X size={18} /></button>
        </div>
        {actions.onCreate ? <SheetItem icon={Plus} label="Create manual tag" hint="Add a piece by hand" onClick={go(actions.onCreate)} testid="tagws-mobile-more-create" /> : null}
        {actions.onImport ? <SheetItem icon={FileSpreadsheet} label="Import Excel" hint="Bulk add pieces" onClick={go(actions.onImport)} testid="tagws-mobile-more-import" /> : null}
        {actions.onRates ? <SheetItem icon={Coins} label="Customer rate cards" hint="Rates for priced tags & Excel" onClick={go(actions.onRates)} testid="tagws-mobile-more-rates" /> : null}
        {actions.onDelete ? <SheetItem icon={Trash2} label="Delete tags" hint="Remove several pieces in one go" onClick={go(actions.onDelete)} testid="tagws-mobile-more-delete" danger /> : null}
        {superAdmin ? (
          <>
            <div className="mx-2 my-2 h-px bg-border" />
            <div className="px-3 pb-1 text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-wide text-muted-foreground">Super admin</div>
            <label className="mx-3 mb-1 flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
              <Building2 size={16} className="text-muted-foreground" />
              <select value={companyId || ''} onChange={(e) => { onCompany(e.target.value); onClose(); }} className="h-8 flex-1 bg-transparent text-sm text-foreground outline-none" data-testid="tagws-mobile-more-company">
                <option value="">Personal tags</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <SheetItem icon={Settings} label="Settings" hint="Users, tag rules, portal hours" onClick={go(actions.onSettings)} testid="tagws-mobile-more-settings" />
            <SheetItem icon={KeyRound} label="View passwords" onClick={go(actions.onPasswords)} testid="tagws-mobile-more-passwords" />
          </>
        ) : null}
        <div className="mx-2 my-2 h-px bg-border" />
        <SheetItem icon={LogOut} label="Sign out" onClick={go(actions.onLogout)} testid="tagws-mobile-more-logout" danger />
      </div>
    </div>
  );
}
